import React, { useState, useEffect } from 'react';
import { initiatePinterestAuth } from '../utils/api';

interface PinterestLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

export const PinterestLoginButton: React.FC<PinterestLoginButtonProps> = ({
  onSuccess,
  onError,
  className = ''
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check localStorage for Pinterest auth status
    const authed = localStorage.getItem('pinterest_authenticated') === 'true';
    setIsAuthenticated(authed);

    // Also check URL params for fresh auth success
    const params = new URLSearchParams(window.location.search);
    if (params.get('pinterest_auth') === 'success') {
      setIsAuthenticated(true);
      localStorage.setItem('pinterest_authenticated', 'true');
      localStorage.setItem('pinterest_auth_time', new Date().toISOString());
      // Clean up URL
      params.delete('pinterest_auth');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handlePinterestLogin = () => {
    if (isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      initiatePinterestAuth();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initiate Pinterest login';
      setError(message);
      setLoading(false);
      onError?.(message);
    }
  };

  if (isAuthenticated) {
    return (
      <div className={`pinterest-login ${className}`}>
        <div className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 font-medium">
          <span>✅</span>
          Signed in with Pinterest
        </div>
      </div>
    );
  }

  return (
    <div className={`pinterest-login ${className}`}>
      <button
        onClick={handlePinterestLogin}
        disabled={loading}
        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 font-medium transition"
      >
        {loading ? (
          <>
            <span className="animate-spin">⏳</span>
            Connecting...
          </>
        ) : (
          <>
            <span>📌</span>
            Sign in with Pinterest
          </>
        )}
      </button>
      {error && (
        <p className="text-red-600 text-sm mt-2">{error}</p>
      )}
    </div>
  );
};

export default PinterestLoginButton;
